<script setup lang="ts">
import type { DataTableColumn } from '@/components/BaseDataTable.vue'
import DataTable from '@/components/BaseDataTable.vue'
import BaseInput from '@/components/BaseInput.vue'
import BaseSelect from '@/components/BaseSelect.vue'
import BaseTextarea from '@/components/BaseTextarea.vue'
import { toast } from '@/composables/baseToast'
import { supabase } from '@/composables/supabaseClient'
import numberToCurrency from '@/helpers/numberToCurrency'
import toastThrowError from '@/helpers/toastThrowError'
import uuidv7 from '@/helpers/uuidv7'
import router from '@/router'
import { useDatabaseStore } from '@/stores/databaseStore'
import { computed } from 'vue'

const props = defineProps<{ itemId: string }>()

const databaseStore = useDatabaseStore()
const item = computed(() => {
  return databaseStore.items?.find((i) => i.id == props.itemId)
})
const itemDivisionSubdivisionCode = computed(() => {
  const subDivision = databaseStore.itemDivisions?.find((d) => d.id == item.value?.item_division_id)
  const division = databaseStore.itemDivisions?.find((d) => d.id == subDivision?.parent_division_id)
  return `${division?.code}.${subDivision?.code}`
})

interface RowData {
  item_cost_source_id: string
  item_cost_id?: string
  item_cost_source_name: string
  labor_type_id?: string | null
  labor_type_name?: string
  labor_productivity_per_hr?: number | null
  equipment_type_id?: string | null
  equipment_type_name?: string
  equipment_productivity_per_hr?: number | null
  material_cost?: number | null
  labor_cost?: number | null
  subcontractor_cost?: number | null
  equipment_cost?: number | null
  other_cost?: number | null
}

// One row per cost source
const dataTableRows = computed<RowData[]>(() => {
  const rows = databaseStore.costSources?.map((costSource) => {
    const itemCost = databaseStore.itemCosts?.find(
      (i) => i.item_id == props.itemId && i.cost_source_id == costSource.id,
    )
    let laborComputedRate = itemCost?.labor_cost
    if (item.value && item.value.labor_type_id) {
      let laborRatePerHr = databaseStore.laborRates?.find(
        (l) => l.cost_source_id == costSource.id && l.labor_type_id == item.value!.labor_type_id,
      )?.rate
      if (!laborRatePerHr) {
        const defaultCostSourceId = databaseStore.costSources?.find(
          (cs) => cs.name == 'Default',
        )?.id
        if (!defaultCostSourceId) throw toastThrowError('Unable to find Default cost source')
        laborRatePerHr = databaseStore.laborRates?.find(
          (l) =>
            l.cost_source_id == defaultCostSourceId && l.labor_type_id == item.value!.labor_type_id,
        )?.rate
      }
      if (!laborRatePerHr)
        throw toastThrowError('No labor rate found. Define one in the Labor Rate settings')
      laborComputedRate = (item.value.labor_productivity_per_hr || 1) * laborRatePerHr
    }
    let equipmentComputedRate = itemCost?.equipment_cost
    if (item.value && item.value.equipment_type_id) {
      let equipmentRatePerHr = databaseStore.equipmentRates?.find(
        (l) =>
          l.cost_source_id == costSource.id && l.equipment_type_id == item.value!.equipment_type_id,
      )?.rate
      if (!equipmentRatePerHr) {
        const defaultCostSourceId = databaseStore.costSources?.find(
          (cs) => cs.name == 'Default',
        )?.id
        if (!defaultCostSourceId) throw toastThrowError('Unable to find Default cost source')
        equipmentRatePerHr = databaseStore.equipmentRates?.find(
          (e) =>
            e.cost_source_id == defaultCostSourceId &&
            e.equipment_type_id == item.value!.equipment_type_id,
        )?.rate
      }
      if (!equipmentRatePerHr)
        throw toastThrowError('No equipment rate found. Define one in the Equipment Rate settings')
      equipmentComputedRate = (item.value.equipment_productivity_per_hr || 1) * equipmentRatePerHr
    }
    return {
      item_cost_source_id: costSource.id,
      item_cost_id: itemCost?.id,
      item_cost_source_name: costSource.name,
      labor_type_id: item.value?.labor_type_id,
      labor_type_name: databaseStore.laborTypes?.find((l) => l.id == item.value?.labor_type_id)
        ?.name,
      labor_productivity_per_hr: item.value?.labor_productivity_per_hr,
      equipment_type_id: item.value?.equipment_type_id,
      equipment_type_name: databaseStore.equipmentTypes?.find(
        (e) => e.id == item.value?.equipment_type_id,
      )?.name,
      equipment_productivity_per_hr: item.value?.equipment_productivity_per_hr,
      material_cost: itemCost?.material_cost,
      labor_cost: laborComputedRate,
      subcontractor_cost: itemCost?.subcontractor_cost,
      equipment_cost: equipmentComputedRate,
      other_cost: itemCost?.other_cost,
    }
  })
  return rows ?? []
})

const costTableColumns = computed<DataTableColumn<RowData>[]>(() => {
  const computedColumns: DataTableColumn<RowData>[] = [
    {
      name: 'Cost Source',
      columnDataKey: 'item_cost_source_name',
      columnType: 'disabled',
    },
    {
      name: 'Material',
      columnDataKey: 'material_cost',
      columnType: 'number',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
    },
    {
      name: 'Labor',
      columnDataKey: 'labor_cost',
      columnType: item.value!.labor_type_id ? 'disabled' : 'number',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
    },
    {
      name: 'Sub',
      columnDataKey: 'subcontractor_cost',
      columnType: 'number',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
    },
    {
      name: 'Equipment',
      columnDataKey: 'equipment_cost',
      columnType: item.value!.equipment_type_id ? 'disabled' : 'number',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
    },
    {
      name: 'Other',
      columnDataKey: 'other_cost',
      columnType: 'number',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
    },
  ]
  return computedColumns
})

async function updateItem() {
  if (!item.value) throw new Error("Item doesn't exist")
  if (!item.value.labor_type_id) item.value.labor_type_id = null
  if (!item.value.equipment_type_id) item.value.equipment_type_id = null
  const result = await supabase
    .from('item')
    .update({
      code: item.value.code,
      accounting_code: item.value.accounting_code,
      name: item.value.name,
      uom_id: item.value.uom_id,
      labor_type_id: item.value.labor_type_id,
      labor_productivity_per_hr: item.value.labor_productivity_per_hr,
      equipment_type_id: item.value.equipment_type_id,
      equipment_productivity_per_hr: item.value.equipment_productivity_per_hr,
    })
    .eq('id', props.itemId)
  if (result.error) throw toastThrowError('Unable to update item', result.error)
  toast('Updated item', { type: 'success' })
}

async function updateCosts(rowData: RowData) {
  // Updates costs by cost source or inserts costs if no costs exist
  if (rowData.item_cost_id) {
    // Update
    const result = await supabase
      .from('item_cost')
      .update({
        material_cost: rowData.material_cost,
        labor_cost: rowData.labor_cost,
        subcontractor_cost: rowData.subcontractor_cost,
        equipment_cost: rowData.equipment_cost,
        other_cost: rowData.other_cost,
      })
      .eq('id', rowData.item_cost_id)
    if (result.error) throw toastThrowError('Unable to update item cost', result.error)
  } else {
    // Insert new
    const result = await supabase.from('item_cost').insert({
      id: uuidv7(),
      item_id: props.itemId!,
      cost_source_id: rowData.item_cost_source_id,
      material_cost: rowData.material_cost,
      labor_cost: rowData.labor_cost,
      subcontractor_cost: rowData.subcontractor_cost,
      equipment_cost: rowData.equipment_cost,
      other_cost: rowData.other_cost,
    })
    if (result.error) throw toastThrowError('Unable to add new item cost', result.error)
  }
}

async function updateLaborAndEquipmentCosts() {
  await updateItem()
  for await (const rowData of dataTableRows.value) {
    // Updates costs by cost source or inserts costs if no costs exist
    if (rowData.item_cost_id) {
      // Update
      const result = await supabase
        .from('item_cost')
        .update({
          labor_cost: rowData.labor_cost,
          equipment_cost: rowData.equipment_cost,
        })
        .eq('id', rowData.item_cost_id)
        .select()
        .single()
      if (result.error) throw toastThrowError('Unable to update item cost', result.error)
      const index = databaseStore.itemCosts?.findIndex((i) => i.id === rowData.item_cost_id)
      if (index == undefined)
        throw toastThrowError('Unable to get index to item cost. Reload page.')
      databaseStore.itemCosts?.splice(index, 1, result.data)
    } else {
      // Insert new
      const result = await supabase
        .from('item_cost')
        .insert({
          id: uuidv7(),
          item_id: props.itemId!,
          cost_source_id: rowData.item_cost_source_id,
          labor_cost: rowData.labor_cost,
          equipment_cost: rowData.equipment_cost,
        })
        .select()
        .single()
      if (result.error) throw toastThrowError('Unable to add new item cost', result.error)
      databaseStore.itemCosts?.push(result.data)
    }
  }
}
</script>

<template>
  <div class="flex h-full w-full flex-col p-3" v-if="item">
    <div class="flex w-full items-center justify-between bg-gray-100 p-3 text-center">
      <h1 class="w-full">{{ item.name }}</h1>
      <button class="cursor-pointer rounded-md p-2 hover:bg-gray-200" @click="router.back()">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="size-6"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
    <fieldset class="flex w-full flex-col gap-3 p-3">
      <div class="flex w-full gap-3">
        <div class="flex w-full flex-col gap-3">
          <BaseInput
            name="Code"
            v-model="item.code"
            required
            @change="updateItem()"
            maxLength="4"
            :formatter="(text) => `${itemDivisionSubdivisionCode}.${text}`"
          />
          <BaseInput name="Name" v-model="item.name" required @change="updateItem()" />
          <BaseSelect
            name="Unit"
            option-key="id"
            option-name="code"
            option-value="id"
            v-model="item.uom_id"
            required
            @change="updateItem()"
            :select-options="databaseStore.unitsOfMeasure"
          />
        </div>
        <div class="flex w-full flex-col gap-3">
          <BaseSelect
            name="Labor Type"
            :select-options="databaseStore.laborTypes"
            option-key="id"
            option-name="name"
            option-value="id"
            v-model="item.labor_type_id"
            allow-blank
            @change="updateLaborAndEquipmentCosts()"
            required
          />
          <BaseInput
            name="Labor Productivity"
            v-model="item.labor_productivity_per_hr"
            :formatter="(value) => `${value}/hr`"
            required
            @change="updateLaborAndEquipmentCosts()"
          />
          <BaseSelect
            name="Equipment Type"
            v-model="item.equipment_type_id"
            required
            @change="updateLaborAndEquipmentCosts()"
            :select-options="databaseStore.equipmentTypes"
            allow-blank
            option-key="id"
            option-value="id"
            option-name="name"
          />
          <BaseInput
            name="Equipment Productivity"
            required
            v-model="item.equipment_productivity_per_hr"
            @change="updateLaborAndEquipmentCosts()"
            :formatter="(value) => `${value}/hr`"
          />
        </div>
      </div>
      <BaseTextarea
        name="Notes"
        v-model="item.description"
        @change="updateItem()"
        :rows="2"
        placeholder="Item notes..."
      />
    </fieldset>
    <div class="flex h-full w-full flex-col">
      <div class="h-full w-full overflow-auto rounded bg-white p-3">
        <DataTable
          table-name="DBItemEdit"
          :columns="costTableColumns"
          :rows="dataTableRows"
          :row-key="'item_cost_source_id'"
          @on-row-update="updateCosts"
        />
      </div>
    </div>
  </div>
</template>
